// src/lib/clients.js
import api from "./api";

/**
 * Ia business-ul curent din:
 *  - parametru (dacă e dat)
 *  - sau localStorage.current_business_id
 *
 * IMPORTANT: FĂRĂ fallback la "1" (periculos).
 */
function getBusinessId(explicitBusinessId) {
  if (explicitBusinessId) return explicitBusinessId;
  if (typeof window === "undefined") return null;
  const raw = window.localStorage.getItem("current_business_id");
  return raw || null;
}

/**
 * Split robust pentru "Nume complet" -> first_name + last_name
 * - dacă e un singur cuvânt, punem același cuvânt și la last_name (backend cere last_name)
 * - dacă e gol, încercăm să derivăm din email/phone ca să nu crape validarea
 */
function splitFullName(fullName, fallbackEmail, fallbackPhone) {
  const raw = String(fullName || "")
    .trim()
    .replace(/\s+/g, " ");

  if (!raw) {
    const fallback =
      (String(fallbackEmail || "").split("@")[0] || "").trim() ||
      String(fallbackPhone || "").trim() ||
      "Client";
    return { first_name: fallback, last_name: fallback };
  }

  const parts = raw.split(" ");
  if (parts.length === 1) {
    return { first_name: parts[0], last_name: parts[0] };
  }

  return {
    first_name: parts[0],
    last_name: parts.slice(1).join(" "),
  };
}

/**
 * Normalizează răspunsul backend → structura folosită în ClientManagement.
 */
function mapClientFromApi(raw) {
  if (!raw) return null;

  const fullName =
    [raw.first_name, raw.last_name].filter(Boolean).join(" ") ||
    raw.name ||
    raw.full_name ||
    raw.email ||
    raw.phone ||
    `Client #${raw.id}`;

  // status derivat și din is_active
  let status = raw.status;
  if (!status && typeof raw.is_active === "boolean") {
    status = raw.is_active ? "active" : "inactive";
  }
  if (!status) status = "active";

  // VIP din backend -> status "vip"
  const isVip = raw.is_vip === true || raw.isVip === true;
  if (isVip) status = "vip";

  const totalAppointments = Number(
    raw.total_appointments ?? raw.appointments_count ?? raw.appointments ?? 0
  );

  const totalSpentRaw = raw.total_spent ?? raw.revenue ?? 0;
  const totalSpent = Number(totalSpentRaw) || 0;

  const created = raw.created_at || raw.created || raw.date_joined || null;

  const vipDiscountRaw = raw.vip_discount_percent ?? raw.vipDiscountPercent ?? null;
  const vipDiscountPercent =
    vipDiscountRaw === null || vipDiscountRaw === undefined
      ? null
      : String(vipDiscountRaw);

  const preferredServiceIds = Array.isArray(raw.preferred_service_ids)
    ? raw.preferred_service_ids
    : Array.isArray(raw.preferredServiceIds)
    ? raw.preferredServiceIds
    : [];

  const preferredServices = Array.isArray(raw.preferred_services)
    ? raw.preferred_services
    : Array.isArray(raw.preferredServices)
    ? raw.preferredServices
    : [];

  return {
    id: raw.id,
    name: fullName,
    email: raw.email || "",
    phone: raw.phone || "",
    status,
    vipDiscountPercent,
    createdAt: created,
    created_at: created,

    totalAppointments,
    totalSpent,

    lastVisit: raw.last_visit || null,

    dateOfBirth: raw.date_of_birth || raw.birth_date || null,
    gender: raw.gender || null,
    address: raw.address || "",
    notes: raw.notes || "",

    preferredServices,
    preferredServiceIds,

    recentAppointments: Array.isArray(raw.recent_appointments)
      ? raw.recent_appointments.map((a) => ({
          id: a.id,
          service: a.service || "",
          date: a.date,
          status: a.status,
        }))
      : undefined,

    preferences: {
      email: raw.pref_email ?? raw.marketing_email ?? raw.allow_email ?? false,
      sms: raw.pref_sms ?? raw.marketing_sms ?? raw.allow_sms ?? false,
      phone: raw.pref_phone ?? raw.marketing_phone ?? raw.allow_phone ?? false,
    },

    _raw: raw,
  };
}

/**
 * Mapare inversă: din structura UI → payload pentru backend.
 * IMPORTANT:
 *  - trimitem mereu last_name
 */
function mapClientToApi(client) {
  const email = client?.email || "";
  const phone = client?.phone || "";

  const { first_name, last_name } = splitFullName(client?.name, email, phone);

  const status = client?.status || "active";
  const is_active = status !== "inactive";
  const is_vip = status === "vip";

  const vipDiscountPercent =
    is_vip && client?.vipDiscountPercent !== undefined
      ? String(client.vipDiscountPercent).trim() === "" || client.vipDiscountPercent === null
        ? null
        : String(client.vipDiscountPercent).trim()
      : null;

  const preferredServiceIds =
    Array.isArray(client?.preferredServiceIds) && client.preferredServiceIds.length
      ? client.preferredServiceIds
          .map((x) => Number(x))
          .filter((n) => Number.isFinite(n))
      : [];

  const preferredServiceIdsFromObjects =
    !preferredServiceIds.length &&
    Array.isArray(client?.preferredServices) &&
    client.preferredServices.some((x) => typeof x === "object" && x?.id != null)
      ? client.preferredServices
          .map((x) => Number(x?.id))
          .filter((n) => Number.isFinite(n))
      : [];

  const finalPreferredIds = preferredServiceIds.length
    ? preferredServiceIds
    : preferredServiceIdsFromObjects;

  const preferredServicesFallback =
    Array.isArray(client?.preferredServices) &&
    client.preferredServices.every((x) => typeof x === "string")
      ? client.preferredServices
      : [];

  return {
    first_name,
    last_name,
    email,
    phone,

    is_active,
    is_vip,
    vip_discount_percent: vipDiscountPercent,

    date_of_birth: client?.dateOfBirth || null,
    gender: client?.gender || null,
    address: client?.address || "",
    notes: client?.notes || "",

    ...(finalPreferredIds.length ? { preferred_service_ids: finalPreferredIds } : {}),
    ...(preferredServicesFallback.length ? { preferred_services: preferredServicesFallback } : {}),

    marketing_email: client?.preferences?.email ?? false,
    marketing_sms: client?.preferences?.sms ?? false,
    marketing_phone: client?.preferences?.phone ?? false,
    // optional: phone re-assign (create only)
    ...(client?.force_phone_reassign || client?.forcePhoneReassign
      ? { force_phone_reassign: true }
      : {}),
    ...(client?.from_client_id != null || client?.fromClientId != null
      ? { from_client_id: Number(client?.from_client_id ?? client?.fromClientId) }
      : {}),
  };
}

/**
 * GET /api/b/clients
 */
export async function listClients({ businessId, params } = {}) {
  const bid = getBusinessId(businessId);
  const { data } = await api.get("/b/clients", {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
    params: params || {},
  });

  const arr = Array.isArray(data)
    ? data
    : Array.isArray(data?.results)
    ? data.results
    : Array.isArray(data?.items)
    ? data.items
    : [];

  return arr.map(mapClientFromApi);
}

/**
 * GET /api/b/clients/:id
 */
export async function getClient(id, { businessId } = {}) {
  const bid = getBusinessId(businessId);
  const { data } = await api.get(`/b/clients/${id}`, {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
  });
  return mapClientFromApi(data);
}

/**
 * POST /api/b/clients
 */
export async function createClient(clientData, { businessId } = {}) {
  const bid = getBusinessId(businessId);
  const payload = mapClientToApi(clientData);

  const { data } = await api.post("/b/clients", payload, {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
  });

  return mapClientFromApi(data);
}

/**
 * PATCH /api/b/clients/:id
 */
export async function updateClient(id, clientData, { businessId } = {}) {
  const bid = getBusinessId(businessId);
  const payload = mapClientToApi(clientData);

  const { data } = await api.patch(`/b/clients/${id}`, payload, {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
  });

  return mapClientFromApi(data);
}

/**
 * DELETE /api/b/clients/:id
 */
export async function deleteClient(id, { businessId } = {}) {
  const bid = getBusinessId(businessId);
  await api.delete(`/b/clients/${id}`, {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
  });
}

/**
 * POST /api/b/clients/import
 * Primește rows din UI/import (ex: {name,email,phone,status,lastVisit})
 * Trimite în backend forma robustă:
 *  { items: [ { row: 1, client: <payload backend> }, ... ] }
 */
/**
 * GET /api/b/clients/suggest?q=...
 * Returns lightweight matches for typeahead.
 * field: "phone" caută doar după telefon.
 */
export async function suggestClients(query, { businessId, limit = 8, field } = {}) {
  const bid = getBusinessId(businessId);
  const q = String(query || "").trim();
  if (!q) return [];

  const { data } = await api.get("/b/clients/suggest", {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
    params: { q, limit, ...(field ? { field } : {}) },
  });

  return Array.isArray(data) ? data : [];
}

/**
 * GET /api/b/clients/phone-check?phone=...
 * Returns { phone_normalized, matches:[...] }
 */
export async function checkClientPhone(phone, { businessId, excludeId } = {}) {
  const bid = getBusinessId(businessId);
  const p = String(phone || "").trim();
  if (!p) return { phone_normalized: "", matches: [] };

  const { data } = await api.get("/b/clients/phone-check", {
    headers: bid ? { "X-Business-Id": String(bid) } : {},
    params: {
      phone: p,
      ...(excludeId ? { exclude_id: excludeId } : {}),
    },
  });

  return data || { phone_normalized: "", matches: [] };
}

export async function importClients(rows, { businessId } = {}) {
  const bid = getBusinessId(businessId);

  const items = (Array.isArray(rows) ? rows : []).map((r, idx) => {
    const rowNo = Number(r?._row ?? r?.row ?? idx + 1);
    return {
      row: Number.isFinite(rowNo) ? rowNo : idx + 1,
      client: mapClientToApi(r),
    };
  });

  const { data } = await api.post(
    "/b/clients/import",
    { items },
    { headers: bid ? { "X-Business-Id": String(bid) } : {} }
  );

  return data;
}

/**
 * Căutare rapidă clienți după termen.
 */
export async function searchClients(term, { businessId } = {}) {
  return listClients({
    businessId,
    params: term ? { search: term } : {},
  });
}
